import { Box, Checkbox, CollapsibleCard, Stack } from '@atgfr8/atg-ui-tailwind';
import { Tab, TabsComponent } from '@components';
import { dormatPoolStore, setDormantPoolStore } from '@store/dormantPool/store';
import { createMemo, createSignal, For, Match, Switch } from 'solid-js';

export const GridViewFilters = () => {
  const [activeTab, setActiveTab] = createSignal(0);
  const tabs = ['Pick', 'Drop', 'Equipment'];

  const originStates = () =>
    Array.isArray(dormatPoolStore.gridView.originStates)
      ? dormatPoolStore.gridView.originStates
      : [];

  const destinationStates = () =>
    Array.isArray(dormatPoolStore.gridView.destinationStates)
      ? dormatPoolStore.gridView.destinationStates
      : [];

  const selectedEquipment = () =>
    Array.isArray(dormatPoolStore.gridView.equipment)
      ? dormatPoolStore.gridView.equipment
      : [];

  const handleOriginChange = (value: string, checked: boolean) => {
    const updated = checked
      ? [...originStates(), value]
      : originStates().filter((v) => v !== value);
    setDormantPoolStore('gridView', 'originStates', updated);
  };

  const handleDestinationChange = (value: string, checked: boolean) => {
    const updated = checked
      ? [...destinationStates(), value]
      : destinationStates().filter((v) => v !== value);
    setDormantPoolStore('gridView', 'destinationStates', updated);
  };

  const handleEquipmentChange = (value: string, checked: boolean) => {
    const updated = checked
      ? [...selectedEquipment(), value]
      : selectedEquipment().filter((v) => v !== value);
    setDormantPoolStore('gridView', 'equipment', updated);
  };

  const combinedStateProvinceOptions = createMemo(() => {
    const { states, provinces } = dormatPoolStore.ATGData;
    return Object.entries({ ...states, ...provinces }).map(([abbr, name]) => ({
      abbr: abbr.toUpperCase(),
      name,
    }));
  });

  const splitIntoColumns = createMemo(() => {
    const items = combinedStateProvinceOptions();
    const colCount = 5;
    const colSize = Math.ceil(items.length / colCount);
    return Array.from({ length: colCount }, (_, i) =>
      items.slice(i * colSize, i * colSize + colSize),
    );
  });

  const splitEquipmentIntoColumns = createMemo(() => {
    const items = dormatPoolStore.ATGData.carrier.equipmentOptions;
    const colCount = 3;
    const colSize = Math.ceil(items.length / colCount);
    return Array.from({ length: colCount }, (_, i) =>
      items.slice(i * colSize, i * colSize + colSize),
    );
  });

  return (
    <Stack spacing={2}>
      <TabsComponent
        border
        tabStyleClasses="rounded-t-lg"
        tabContainerStyles="ml-1 items-center w-0!"
        activeStyling="bg-[#ffe8b7]"
        tabs={tabs.map((item) => (
          <Tab>{item}</Tab>
        ))}
        activeTab={activeTab()}
        onTabClick={setActiveTab}
      />

      <Switch>
        <Match when={activeTab() === 0}>
          <CollapsibleCard
            startTitle="Filter Pickup State/Provinces"
            contentClass="p-6!"
            class="mb-2"
          >
            <Box class="flex flex-wrap" justifyContent="space-between">
              <For each={splitIntoColumns()}>
                {(column) => (
                  <Box class="flex flex-col" style={{ 'min-width': '120px' }}>
                    <For each={column}>
                      {(option) => (
                        <Checkbox
                          size="xs"
                          color="info"
                          label={option.name}
                          checked={originStates().includes(option.abbr)}
                          onChange={(checked) =>
                            handleOriginChange(option.abbr, checked)
                          }
                          dataTestId={`grid-view-filters-pick-checkboxInput-${option.name}`}
                        />
                      )}
                    </For>
                  </Box>
                )}
              </For>
            </Box>
          </CollapsibleCard>
        </Match>

        <Match when={activeTab() === 1}>
          <CollapsibleCard
            startTitle="Filter Drop State/Provinces"
            contentClass="p-6!"
            class="mb-2"
          >
            <Box class="flex flex-wrap" justifyContent="space-between">
              <For each={splitIntoColumns()}>
                {(column) => (
                  <Box class="flex flex-col" style={{ 'min-width': '120px' }}>
                    <For each={column}>
                      {(option) => (
                        <Checkbox
                          size="xs"
                          color="info"
                          label={option.name}
                          checked={destinationStates().includes(option.abbr)}
                          onChange={(checked) =>
                            handleDestinationChange(option.abbr, checked)
                          }
                          dataTestId={`grid-view-filters-drop-checkboxInput-${option.name}`}
                        />
                      )}
                    </For>
                  </Box>
                )}
              </For>
            </Box>
          </CollapsibleCard>
        </Match>

        <Match when={activeTab() === 2}>
          <CollapsibleCard
            startTitle="Filter Equipment Types"
            contentClass="p-6!"
            class="mb-2"
          >
            <Box class="flex flex-wrap" justifyContent="space-between">
              <For each={splitEquipmentIntoColumns()}>
                {(column) => (
                  <Box class="flex flex-col" style={{ 'min-width': '120px' }}>
                    <For each={column}>
                      {(equipment) => (
                        <Checkbox
                          size="xs"
                          color="info"
                          label={equipment.name}
                          checked={selectedEquipment().includes(equipment.name)}
                          onChange={(checked) =>
                            handleEquipmentChange(equipment.name, checked)
                          }
                          dataTestId={`grid-view-filters-equipment-checkboxInput-${equipment.name}`}
                        />
                      )}
                    </For>
                  </Box>
                )}
              </For>
            </Box>
          </CollapsibleCard>
        </Match>
      </Switch>
    </Stack>
  );
};
